import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Clock, AlertTriangle, Home, FileText, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PagamentoEsitoPage() {
  const [params] = useSearchParams();
  const statusFromQuery = params.get("status") || "";
  const codTrans = params.get("codTrans") || params.get("cod_trans") || "";
  const [loading, setLoading] = useState(true);
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!codTrans) {
        setLoading(false);
        return;
      }
      try {
        // Polling fino a 4 tentativi per attendere la notifica server-to-server
        // (Nexi la invia in parallelo al redirect utente; talvolta arriva poco dopo)
        for (let i = 0; i < 5; i++) {
          const { data } = await axios.get(`${API}/nexi/transaction/${codTrans}`);
          if (!mounted) return;
          setTx(data);
          if (data?.status === "paid" || data?.status === "failed") break;
          await new Promise((r) => setTimeout(r, 1500));
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Transazione non trovata");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [codTrans]);

  // Derivazione stato finale
  const status = (() => {
    if (tx?.status === "paid") return "success";
    if (tx?.status === "failed") return "failed";
    if (statusFromQuery === "success" || statusFromQuery === "failed" || statusFromQuery === "invalid")
      return statusFromQuery;
    if (tx?.status === "pending") return "pending";
    return "unknown";
  })();

  const importoEur = tx?.amount_eur ?? (tx?.amount_cents ? tx.amount_cents / 100 : null);

  const cfg = {
    success: {
      icon: <CheckCircle2 className="w-20 h-20 text-emerald-400" />,
      title: "Pagamento Completato",
      subtitle: "La tua polizza è stata pagata correttamente. Riceverai la conferma via email a breve.",
      accent: "from-emerald-500/20 to-emerald-400/5",
      border: "border-emerald-500/30",
    },
    failed: {
      icon: <XCircle className="w-20 h-20 text-rose-400" />,
      title: "Pagamento Non Riuscito",
      subtitle: "Il pagamento non è stato autorizzato. Puoi riprovare con un metodo diverso.",
      accent: "from-rose-500/20 to-rose-400/5",
      border: "border-rose-500/30",
    },
    pending: {
      icon: <Clock className="w-20 h-20 text-amber-400" />,
      title: "Pagamento in Elaborazione",
      subtitle: "Stiamo attendendo la conferma da Nexi. Non chiudere questa pagina.",
      accent: "from-amber-500/20 to-amber-400/5",
      border: "border-amber-500/30",
    },
    invalid: {
      icon: <AlertTriangle className="w-20 h-20 text-orange-400" />,
      title: "Verifica Firma Fallita",
      subtitle: "La firma digitale della transazione non è valida. Contatta il nostro supporto.",
      accent: "from-orange-500/20 to-orange-400/5",
      border: "border-orange-500/30",
    },
    unknown: {
      icon: <AlertTriangle className="w-20 h-20 text-slate-400" />,
      title: "Stato Sconosciuto",
      subtitle: "Non siamo riusciti a recuperare lo stato della tua transazione.",
      accent: "from-slate-500/20 to-slate-400/5",
      border: "border-slate-500/30",
    },
  }[status];

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-24">
        <div
          className={
            "max-w-xl w-full rounded-3xl border p-10 bg-gradient-to-br backdrop-blur-md " +
            cfg.border +
            " " +
            cfg.accent
          }
          data-testid="nexi-esito-panel"
        >
          {loading ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <Loader2 className="w-12 h-12 animate-spin text-amber-400" />
              <p className="text-slate-300">Verifica in corso della transazione…</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center gap-4 mb-6">
                {cfg.icon}
                <h1 className="text-3xl font-bold text-center" data-testid="nexi-esito-title">
                  {cfg.title}
                </h1>
                <p className="text-slate-300 text-center">{cfg.subtitle}</p>
              </div>

              {(tx || importoEur) && (
                <div className="mt-6 border-t border-white/10 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {importoEur != null && (
                    <Info label="Importo" value={`€ ${Number(importoEur).toFixed(2)}`} />
                  )}
                  {tx?.cod_trans && <Info label="Codice Transazione" value={tx.cod_trans} mono />}
                  {tx?.cod_aut && <Info label="Autorizzazione" value={tx.cod_aut} mono />}
                  {tx?.tipo_polizza && <Info label="Prodotto" value={tx.tipo_polizza} />}
                </div>
              )}

              {err && <p className="text-rose-300 text-sm mt-4">{err}</p>}

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link
                  to="/"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-xl transition-colors"
                  data-testid="nexi-esito-home-btn"
                >
                  <Home className="w-4 h-4" />
                  Torna alla home
                </Link>
                {status === "success" && (
                  <a
                    href="mailto:colucci@colucciassicuratori.com?subject=Richiesta%20ricevuta%20polizza"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold py-3 rounded-xl transition-colors"
                    data-testid="nexi-esito-email-btn"
                  >
                    <FileText className="w-4 h-4" />
                    Richiedi ricevuta
                  </a>
                )}
                {status === "failed" && (
                  <button
                    onClick={() => window.history.back()}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold py-3 rounded-xl transition-colors"
                    data-testid="nexi-esito-retry-btn"
                  >
                    Riprova pagamento
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/10">
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={"mt-1 text-sm font-medium text-white " + (mono ? "font-mono text-xs break-all" : "")}>
        {value}
      </p>
    </div>
  );
}
